/**
 * Presets de interface por org (white-label).
 *
 * `organizations.ui_preset` (migration 20260930100000) diz QUAL preset a org usa; o que cada
 * preset muda mora aqui. `null` = Vertto padrão, e nenhum ponto do app muda de comportamento.
 *
 * Um preset tem duas partes:
 *   - marca (sempre): tema (atributo `data-brand` no <html>, tokens em
 *     src/styles/themes/<brand>.css), fontes, favicon, logos e telas de carregamento;
 *   - `shell` (opcional): menu enxuto, bloqueio de rota (OrgPresetRouteGuard), abas de
 *     Configurações filtradas, sem telefone no header e sem tours.
 * Preset com `shell: null` muda só o visual: a org segue com o menu e as rotas do Vertto.
 *
 * Por que coluna e não UUID no código: a regra do time é flag por org com default seguro
 * (AGENTS.md §8.8). Um segundo escritório entra no mesmo preset com um UPDATE.
 */
import {
  LayoutDashboard, CheckSquare, Users, Building2, DollarSign, FileSignature,
  MessageCircle, Bot, CalendarPlus, BookOpen,
} from 'lucide-react';
import canarioAtticus from '@/assets/atticus/atticus-canario.svg';
import horizontalOlivaAtticus from '@/assets/atticus/atticus-horizontal-oliva.svg';

export type OrgPresetId = 'atticus' | 'atticus_marca';

export type OrgPresetNavItem = {
  name: string;
  href: string;
  icon: typeof LayoutDashboard;
};

export type OrgPresetNavGroup = {
  id: string;
  label: string;
  icon: typeof LayoutDashboard;
  items: OrgPresetNavItem[];
};

/** Interface enxuta: o que um escritório que usa só parte do CRM enxerga. */
export type OrgPresetShell = {
  homeRoute: string;
  navGroups: OrgPresetNavGroup[];
  /**
   * Rotas liberadas. Casa a rota exata e tudo abaixo dela (`/deals` libera `/deals/123`).
   * Qualquer outra rota do shell do CRM redireciona para `homeRoute`.
   */
  allowedRoutePrefixes: string[];
  /** Exceções dentro de um prefixo liberado. */
  blockedRoutePrefixes: string[];
  /** Valores de aba de /settings que aparecem (`value` das abas em pages/Settings.tsx). */
  settingsTabs: string[];
};

export type OrgPreset = {
  id: OrgPresetId;
  /** Nome exibido no título da aba e no sufixo do PageHeader. */
  brandName: string;
  /** Valor de `data-brand` no <html>; os tokens ficam sob `html[data-brand="…"]`. */
  brandAttr: string;
  /** Folha do Google Fonts carregada só quando o preset está ativo. */
  fontsHref: string;
  /** Favicon servido de /public. */
  faviconHref: string;
  /**
   * Telas de carregamento (splash de entrada, "Verificando autenticação", "Carregando
   * organização"): marca no lugar do "tt" do Vertto e cores fixas. Fixas, e não os tokens do
   * tema, porque essas telas aparecem antes de o `data-brand` estar no <html>.
   */
  loading: { markSrc: string; background: string; accent: string; muted: string };
  /**
   * Logo horizontal para fundo claro, usado nas páginas públicas (agendamento). Fica no
   * preset porque a página pública não passa pelo shell do CRM e não tem outro lugar para
   * saber qual marca desenhar.
   */
  publicLogo: { src: string; width: number; height: number };
  /** Rótulo ao lado do logo na navbar (ex.: "HUB"); `null` = só o logo. */
  navbarTag: string | null;
  /** Menu, rotas e abas próprios; `null` = interface padrão do Vertto, só com a marca trocada. */
  shell: OrgPresetShell | null;
};

/** Menu e rotas da Atticus.hub: só IA de atendimento, WhatsApp, cadastro, documentos e agenda. */
const ATTICUS_HUB_SHELL: OrgPresetShell = {
  homeRoute: '/dashboard',
  navGroups: [
    {
      id: 'inicio',
      label: 'Início',
      icon: LayoutDashboard,
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Atividades', href: '/activities', icon: CheckSquare },
        // Links de agendamento (/book/:slug). A página pública já sai com a marca do preset.
        // O item só aparece para quem tem `desempenho.gestao_agenda.manage` (Sidebar).
        { name: 'Agendamento', href: '/agendamento', icon: CalendarPlus },
      ],
    },
    {
      id: 'clientes',
      label: 'Clientes',
      icon: Users,
      items: [
        { name: 'Leads', href: '/leads', icon: Users },
        { name: 'Empresas', href: '/companies', icon: Building2 },
        { name: 'Negócios', href: '/deals', icon: DollarSign },
        { name: 'Documentos', href: '/documentos', icon: FileSignature },
      ],
    },
    {
      id: 'atendimento',
      label: 'Atendimento',
      icon: MessageCircle,
      items: [
        { name: 'WhatsApp', href: '/chat', icon: MessageCircle },
        // Só admin vê (gate em Sidebar.tsx › stableIsRouteEnabled): é a configuração da IA.
        { name: 'IA de atendimento', href: '/agentes', icon: Bot },
      ],
    },
    {
      // Playbooks da operação (docs/specs/materiais-documentacao-playbook.md). Aparece só com a
      // flag `playbooks_enabled` da org e a chave `materiais.playbooks.view` (Sidebar).
      id: 'materiais',
      label: 'Materiais',
      icon: BookOpen,
      items: [
        { name: 'Playbooks', href: '/materiais/playbooks', icon: BookOpen },
      ],
    },
  ],
  allowedRoutePrefixes: [
    '/',
    '/dashboard',
    '/dashboard-v2', // redirect legado para /dashboard
    '/activities',
    '/agendamento', // links de agendamento + /agendamento/disponibilidade + editor
    '/leads',
    '/companies',
    '/deals',
    '/documentos',
    '/contratos', // mesma tela de /documentos
    '/chat',
    '/agentes',
    '/materiais',
    '/settings',
    '/configuracoes/integracoes',
    '/calls', // transcrição de ligação aberta a partir do negócio
    '/meetings', // transcrição de reunião aberta a partir do negócio
    '/notifications',
    '/profile',
    '/invite',
    '/auth',
    // `/admin/organizations` é do super-admin; `/admin` puro redireciona para /settings.
    '/admin',
  ],
  blockedRoutePrefixes: ['/activities/focus'],
  settingsTabs: [
    'organization',
    'users',
    'custom-fields',
    'negocios',
    'activities',
    'whatsapp-instances',
    'integracoes',
    'contratos',
    'ai',
    'notifications',
  ],
};

/** Marca Atticus (brandbook Direção Oliva v2.0), comum aos dois presets do escritório. */
const ATTICUS_BRAND = {
  brandAttr: 'atticus',
  fontsHref:
    'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,300;9..144,500&family=IBM+Plex+Mono:wght@400;500&display=swap',
  faviconHref: '/atticus-favicon.svg',
  // Canário em Oliva sobre Papel (brandbook: símbolo para avatar, favicon e marca d'água).
  loading: { markSrc: canarioAtticus, background: '#F7F5F0', accent: '#22300B', muted: '#5C5A52' },
  // Lockup horizontal em Oliva (o original do brandbook é Papel, para fundo escuro).
  publicLogo: { src: horizontalOlivaAtticus, width: 156, height: 28 },
} satisfies Partial<OrgPreset>;

/** Org Atticus.hub (fd4c73ab): marca e interface enxuta. */
const ATTICUS: OrgPreset = {
  ...ATTICUS_BRAND,
  id: 'atticus',
  brandName: 'Atticus.hub',
  navbarTag: 'HUB',
  shell: ATTICUS_HUB_SHELL,
};

/**
 * Org "Atticus" (08ae88ea, operação comercial): só a marca do escritório. Menu, rotas,
 * telefone e tours seguem os do Vertto.
 */
const ATTICUS_MARCA: OrgPreset = {
  ...ATTICUS_BRAND,
  id: 'atticus_marca',
  brandName: 'Atticus',
  navbarTag: null,
  shell: null,
};


const ORG_PRESETS: Record<OrgPresetId, OrgPreset> = {
  atticus: ATTICUS,
  atticus_marca: ATTICUS_MARCA,
};

/** Valor desconhecido (build antigo, typo no banco) cai em `null`: Vertto padrão, nunca meio preset. */
export function getOrgPreset(uiPreset: string | null | undefined): OrgPreset | null {
  if (!uiPreset || !Object.prototype.hasOwnProperty.call(ORG_PRESETS, uiPreset)) return null;
  return ORG_PRESETS[uiPreset as OrgPresetId];
}

/**
 * O preset precisa ser conhecido ANTES de a org carregar: a splash de entrada aparece enquanto
 * as orgs ainda estão sendo buscadas, e sem isto o "tt" do Vertto piscaria antes do canário.
 * Guardamos o par (org, preset) da última visita e só usamos se a org guardada para abrir
 * (`current_org_id`, a mesma que o OrganizationContext vai carregar) for a mesma.
 */
const CHAVE_PRESET_GUARDADO = 'org_ui_preset_cache';

export function rememberOrgPreset(orgId: string, uiPreset: string | null | undefined): void {
  try {
    localStorage.setItem(CHAVE_PRESET_GUARDADO, JSON.stringify({ orgId, uiPreset: uiPreset ?? null }));
  } catch {
    // Storage bloqueado (aba anônima, política do navegador): só perde o palpite da splash.
  }
}

export function getRememberedOrgPreset(orgIdToOpen: string | null): OrgPreset | null {
  if (!orgIdToOpen) return null;
  try {
    const raw = localStorage.getItem(CHAVE_PRESET_GUARDADO);
    if (!raw) return null;
    const { orgId, uiPreset } = JSON.parse(raw) as { orgId?: string; uiPreset?: string | null };
    return orgId === orgIdToOpen ? getOrgPreset(uiPreset) : null;
  } catch {
    return null;
  }
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  if (prefix === '/') return pathname === '/';
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Preset sem `shell` não restringe rota nenhuma. */
export function isRouteAllowedByPreset(preset: OrgPreset, pathname: string): boolean {
  const { shell } = preset;
  if (!shell) return true;
  if (shell.blockedRoutePrefixes.some((p) => matchesPrefix(pathname, p))) return false;
  return shell.allowedRoutePrefixes.some((p) => matchesPrefix(pathname, p));
}

export function isNavItemActive(pathname: string, href: string): boolean {
  return matchesPrefix(pathname, href);
}
